import {applySnapshot, types} from 'mobx-state-tree';
import moment from 'moment';
import {rootStore} from '.';
import { getLocalizedField } from 'helpers/localizedField';
import CompanyService from 'services/CompanyService';
import {processDataWithReplaceDataFields} from '../pages/CommonMenu/Member/AnnualLeavePromotion/helpers';
import GroupService from 'services/GroupService';
import get from 'lodash/get';

/** 상세 엑셀 행에 붙일 직원 이름(번역 필드 포함). 엑셀의 이름 칸은 getUserDisplayName으로 그린다 */
const pickUserNameFields = (user) => ({
    userName: user?.userName || '',
    userNameEngFirst: user?.userNameEngFirst,
    userNameEngLast: user?.userNameEngLast,
    userNameVie: user?.userNameVie,
    userNameChn: user?.userNameChn,
});

export const AnnualLeavePromotion = types
    .model('AnnualLeavePromotion', {
        // Filter states grouped by tab - each tab has its own filter parameters
        filterParams: types.optional(types.model('FilterParams', {
            tab: types.optional(types.string, 'rest'), // Current active tab

            // Rest tab filters
            rest: types.optional(types.model('RestFilters', {
                selectedYear: types.optional(types.string, moment().format('YYYY')),
                searchUser: types.optional(types.string, ''),
                employType: types.optional(types.string, ''),
                workPlace: types.optional(types.maybe(types.number), 0),
                emailType: types.optional(types.string, ''),
                receiptStatus: types.optional(types.string, ''),
                annualLeavePlanStatus: types.optional(types.string, ''),
                targetEmployee: types.optional(types.string, ''),
                workStatus: types.optional(types.string, 'SUCCESS'),
                remainingType: types.optional(types.string, ''),
            }), {}),

            // Random tab filters
            random: types.optional(types.model('RandomFilters', {
                selectedYear: types.optional(types.string, moment().format('YYYY')),
                searchUser: types.optional(types.string, ''),
                employType: types.optional(types.string, ''),
                workPlace: types.optional(types.maybe(types.number), 0),
                annualLeavePlanStatus: types.optional(types.string, ''),
                targetEmployee: types.optional(types.string, ''),
                workStatus: types.optional(types.string, 'SUCCESS'),
                remainingType: types.optional(types.string, ''),
            }), {}),

            // Labor tab filters
            labor: types.optional(types.model('LaborFilters', {
                selectedYear: types.optional(types.string, moment().format('YYYY')),
                searchUser: types.optional(types.string, ''),
                sendStatus: types.optional(types.string, ''),
                receiptStatus: types.optional(types.string, ''),
                /*
                employType: types.optional(types.string, ''),
                workPlace: types.optional(types.maybe(types.number), 0),
                workStatus: types.optional(types.string, 'SUCCESS'),
                */
            }), {}),

            // Email Status tab filters
            emailStatus: types.optional(types.model('EmailStatusFilters', {
                selectedYear: types.optional(types.string, moment().format('YYYY')),
                searchUser: types.optional(types.string, ''),
                employType: types.optional(types.string, ''),
                workPlace: types.optional(types.maybe(types.number), 0),
                workStatus: types.optional(types.string, 'SUCCESS'),
                emailType: types.optional(types.string, ''),
                sendStatus: types.optional(types.string, ''),
                agreeStatus: types.optional(types.string, ''),
            }), {}),
        }), {}),

        // Data lists
        year: types.optional(types.array(types.number), []),
        employList: types.optional(types.array(types.frozen()), []),
        workPlaceList: types.optional(types.array(types.frozen()), []),
        templateList: types.optional(types.array(types.frozen()), []),
        commCodeList: types.optional(types.array(types.frozen()), []),

        // Table data - tab specific
        restData: types.optional(types.array(types.frozen()), []),
        restDataCount: types.optional(types.number, 0),
        randomData: types.optional(types.array(types.frozen()), []),
        randomDataCount: types.optional(types.number, 0),
        laborData: types.optional(types.array(types.frozen()), []),
        laborDataCount: types.optional(types.number, 0),
        emailStatusData: types.optional(types.array(types.frozen()), []),
        emailStatusDataCount: types.optional(types.number, 0),

        // Common data
        page: types.optional(types.number, 1),
        size: types.optional(types.number, 500), // default bulk size; emailStatus tab uses its own capped size
        isLoading: types.optional(types.boolean, false),
        isBackgroundLoading: types.optional(types.boolean, false),
        // Internal counter to identify and cancel outdated background fetch runs
        backgroundFetchRunId: types.optional(types.number, 0),

        // Selected user for detail modal
        selectedUser: types.optional(types.maybe(types.frozen()), []),
        selectedEmailDetail: types.optional(types.maybe(types.frozen()), []),
        selectedStandardHour: types.optional(types.number, 0),
        selectedDate: types.optional(types.frozen(), null),
        userIdsForAction: types.optional(types.array(types.number), []),
        checkedUsers: types.optional(types.array(types.frozen()), []),
    })
    .views((self) => ({
        get get() {
            try {
                return { ...self };
            } catch (err) {
                console.error(err);
                return err;
            }
        },

        // Convenience getters for backward compatibility
        get tab() {
            return self.filterParams.tab;
        },

        // Get current tab's filter parameters
        get currentTabFilters() {
            return self.filterParams[self.filterParams.tab];
        },

        // Tab-specific data getters
        get currentTabData() {
            switch (self.filterParams.tab) {
                case 'rest':
                    return self.restData;
                case 'random':
                    return self.randomData;
                case 'labor':
                    return self.laborData;
                case 'emailStatus':
                    return self.emailStatusData;
                default:
                    return self.restData;
            }
        },

        get currentTabDataCount() {
            switch (self.filterParams.tab) {
                case 'rest':
                    return self.restDataCount;
                case 'random':
                    return self.randomDataCount;
                case 'labor':
                    return self.laborDataCount;
                case 'emailStatus':
                    return self.emailStatusDataCount;
                default:
                    return 0;
            }
        },
    }))
    .actions((self) => ({
        reset() {
            console.log('RESET AnnualLeavePromotion MODEL');
            applySnapshot(self, {
                year: self.year.slice(),
            });
        },

        // Tab actions
        setTab(tab) {
            self.filterParams.tab = tab;
        },

        // Filter actions - Combined setter for current tab
        setFilterParams(params) {
            // Only cancel if a background pagination is in progress
            if (self.isBackgroundLoading) {
                this.cancelBackgroundLoading();
            }
            const currentFilters = self.filterParams[self.filterParams.tab];
            if (!currentFilters) {
                return;
            }
            Object.keys(params).forEach(key => {
                if (key in currentFilters) {
                    if (key === 'workPlace') {
                        currentFilters[key] = Number(params[key]);
                    } else {
                        currentFilters[key] = params[key];
                    }
                }
            });
        },

        // Selected user actions
        setSelectedUser(user) {
            self.selectedUser = user;
        },
        setSelectedStandardHour(value) {
            self.selectedStandardHour = value;
        },
        setCheckedUsers(users) {
            self.checkedUsers = users;
        },
        clearSelectedUser() {
            self.selectedUser = null;
            self.selectedEmailDetail = [];
            self.selectedStandardHour = 0;
        },

        setSelectedEmailDetail(detail) {
            self.selectedEmailDetail = detail;
        },

        clearSelectedEmailDetail() {
            self.selectedEmailDetail = null;
        },

        // Add these actions after the existing ones
        setSelectedDate(date) {
            self.selectedDate = date;
        },

        // Data actions
        setYear(yearList) {
            self.year = yearList;
        },

        setEmployList(list) {
            self.employList.replace(list || []);
        },

        setWorkPlaceList(list) {
            self.workPlaceList = list;
        },

        setTemplateList(list) {
            self.templateList = list;
        },

        setCommCodeList(list) {
            self.commCodeList.replace(list || []);
        },

        // Tab-specific data setters
        setRestData(data) {
            const processedData = processDataWithReplaceDataFields(data);
            self.restData.replace(processedData);
        },

        appendRestData(data) {
            const processedData = processDataWithReplaceDataFields(data);
            self.restData.push(...processedData);
        },

        setRestDataCount(count) {
            self.restDataCount = count;
        },

        setRandomData(data) {
            const processedData = processDataWithReplaceDataFields(data);
            self.randomData.replace(processedData);
        },

        appendRandomData(data) {
            const processedData = processDataWithReplaceDataFields(data);
            self.randomData.push(...processedData);
        },

        setRandomDataCount(count) {
            self.randomDataCount = count;
        },

        setLaborData(data) {
            const processedData = processDataWithReplaceDataFields(data);
            self.laborData.replace(processedData);
        },

        appendLaborData(data) {
            const processedData = processDataWithReplaceDataFields(data);
            self.laborData.push(...processedData);
        },

        setLaborDataCount(count) {
            self.laborDataCount = count;
        },

        setEmailStatusData(data) {
            const processedData = processDataWithReplaceDataFields(data);
            self.emailStatusData.replace(processedData);
        },

        appendEmailStatusData(data) {
            const processedData = processDataWithReplaceDataFields(data);
            self.emailStatusData.push(...processedData);
        },

        setEmailStatusDataCount(count) {
            self.emailStatusDataCount = count;
        },

        // Set data for current tab
        setCurrentTabData(data, count) {
            switch (self.filterParams.tab) {
                case 'rest':
                    self.setRestData(data);
                    self.setRestDataCount(count);
                    break;
                case 'random':
                    self.setRandomData(data);
                    self.setRandomDataCount(count);
                    break;
                case 'labor':
                    self.setLaborData(data);
                    self.setLaborDataCount(count);
                    break;
                case 'emailStatus':
                    self.setEmailStatusData(data);
                    self.setEmailStatusDataCount(count);
                    break;
                default:
                    self.setRestData(data);
                    self.setRestDataCount(count);
            }
        },

        setPage(page) {
            self.page = page;
        },

        setIsLoading(loading) {
            self.isLoading = loading;
        },

        setIsBackgroundLoading(loading) {
            self.isBackgroundLoading = loading;
        },

        // Public action to cancel background staged loading
        cancelBackgroundLoading() {
            // Bump run id so existing background loops detect cancellation and stop
            self.backgroundFetchRunId = self.backgroundFetchRunId + 1;
            self.isBackgroundLoading = false;
        },

        setUserIdsForAction(userIds) {
            self.userIdsForAction = userIds;
        },

        // Reset filters for current tab
        resetFilters() {
            const resetMethods = {
                rest: () => this.resetRestFilters(),
                random: () => this.resetRandomFilters(),
                labor: () => this.resetLaborFilters(),
                emailStatus: () => this.resetEmailStatusFilters(),
            };

            resetMethods[self.filterParams.tab]?.();
        },

        // Reset filters for rest tab
        resetRestFilters() {
            const currentFilters = self.filterParams.rest;
            currentFilters.selectedYear = moment().format('YYYY');
            currentFilters.searchUser = '';
            currentFilters.employType = '';
            currentFilters.workPlace = 0;
            currentFilters.emailType = '';
            currentFilters.annualLeavePlanStatus = '';
            currentFilters.targetEmployee = '';
            currentFilters.workStatus = 'SUCCESS';
            currentFilters.remainingType = '';
        },

        // Reset filters for random tab
        resetRandomFilters() {
            const currentFilters = self.filterParams.random;
            currentFilters.selectedYear = moment().format('YYYY');
            currentFilters.searchUser = '';
            currentFilters.employType = '';
            currentFilters.workPlace = 0;
            currentFilters.annualLeavePlanStatus = '';
            currentFilters.targetEmployee = '';
            currentFilters.workStatus = 'SUCCESS';
            currentFilters.remainingType = '';
        },

        // Reset filters for labor tab
        resetLaborFilters() {
            const currentFilters = self.filterParams.labor;
            currentFilters.selectedYear = moment().format('YYYY');
            currentFilters.searchUser = '';
            currentFilters.sendStatus = '';
            currentFilters.agreeStatus = '';
            /*
            currentFilters.employType = '';
            currentFilters.workPlace = 0;
            currentFilters.workStatus = 'SUCCESS';
            */
        },

        // Reset filters for emailStatus tab
        resetEmailStatusFilters() {
            const currentFilters = self.filterParams.emailStatus;
            currentFilters.selectedYear = moment().format('YYYY');
            currentFilters.searchUser = '';
            currentFilters.employType = '';
            currentFilters.workPlace = 0;
            currentFilters.workStatus = 'SUCCESS';
            currentFilters.emailType = '';
            currentFilters.sendStatus = '';
            currentFilters.receiptStatus = '';
        },

        // Reset data when changing tabs
        resetData() {
            self.page = 1;
            self.setUserIdsForAction([]);
        },

        // API calls
        async getCommCodes() {
            const { company } = rootStore;
            try {
                const [empRes, promotionRes] = await Promise.all([
                    CompanyService.getCommCode(
                        company?.get?.uuid || 1000,
                        'EMP_TYPE'
                    ),
                    CompanyService.getCommCodeNew({
                        companyId: company?.get?.id,
                        commCodeType: 'PROMOTION_TARGET_TYPE'
                    })
                ]);

                self.setEmployList(
                    empRes?.data?.map(v => ({
                        value: v?.comm_code,
                        label: getLocalizedField(v, 'comm_code_name')
                    })) || []
                );

                self.setCommCodeList(
                    promotionRes?.data
                        ?.filter(v => v?.etc1 === 'Y')
                        ?.sort((a, b) => Number(a?.sort_no || 0) - Number(b?.sort_no || 0))
                        ?.map(v => ({
                            value: v?.comm_code,
                            label: getLocalizedField(v, 'comm_code_name'),
                            // 휴가현황에서 넘어올 때 한글 이름으로 찾는다(initialize의 targetEmployeeLabel)
                            rawLabel: v?.comm_code_name
                        })) || []
                );

                return promotionRes?.data || [];
            } catch (error) {
                console.error('Error fetching employee types:', error);
                self.setEmployList([]);
                self.setCommCodeList([]);
                return [];
            }
        },

        async getWorkPlace() {
            const { company } = rootStore;
            try {
                const response = await CompanyService.getWorkingArea(company.id);
                if (response.data) {
                    const workPlaceList = response?.data?.map((v) => ({
                        value: v?.id,
                        label: v?.name === '' ? '본사' : v?.name,
                    })) || [];

                    self.setWorkPlaceList(workPlaceList);
                    return workPlaceList;
                }
                return [];
            } catch (error) {
                console.error('Error fetching work places:', error);
                return [];
            }
        },

        async getYear() {
            const { company } = rootStore;
            try {
                let yearData = [];
                let i = 0;
                while (
                    parseInt(
                        moment(company?.get?.companyAnniversary || '').format('YYYY')
                    ) <= parseInt(moment().format('YYYY')) - i
                ) {
                    yearData.push(parseInt(moment().format('YYYY')) - i);
                    i++;
                }

                self.setYear(yearData);
                return yearData;
            } catch (error) {
                console.error('Error generating year list:', error);
                return [];
            }
        },

        /**
         * Load initial data and trigger background loading for remaining pages
         */
        async getLeavePromotionList() {
            // Cancel any in-progress background pagination
            this.cancelBackgroundLoading();

            // Start first page loading
            self.setIsLoading(true);

            const companyId = rootStore.company.get.id;
            const currentFilters = self.filterParams[self.filterParams.tab];

            if (!currentFilters) {
                self.setIsLoading(false);
                return [];
            }

            const area = Number(currentFilters?.workPlace) || null;
            const { workPlace, ...rest } = currentFilters;

            try {
                // Determine which API to call based on tab
                const apiNames = {
                    random: 'getAssignmentLeaveList',
                    emailStatus: 'getEmailStatusList',
                    rest: 'getLeavePromotionList',
                    labor: 'getLeavePromotionList',
                };

                // First page load - independent of background pagination
                const firstResponse = await CompanyService[apiNames[self.filterParams.tab]]({
                    companyId,
                    page: 1,
                    size: self.size,
                    filters: {
                        ...rest,
                        area,
                        tab: self.filterParams.tab
                    }
                });

                const totalCount = firstResponse?.count || 0;

                // Always set initial data - this is separate from background loading
                self.setCurrentTabData(firstResponse?.data || [], totalCount);

                // If there are more pages, load them in background with separate run ID tracking
                if (totalCount > self.size) {
                    self.fetchBackgroundData();
                }

                return firstResponse?.data || [];
            } catch (error) {
                console.error('Error loading leave promotion list:', error);
                return [];
            } finally {
                // Clear first page loading - independent of background
                self.setIsLoading(false);
            }
        },

        /**
         * Fetch remaining pages in background after first page is displayed
         */
        fetchBackgroundData() {
            const companyId = rootStore.company.get.id;
            const currentFilters = self.filterParams[self.filterParams.tab];
            const area = Number(currentFilters?.workPlace) || null;
            const { workPlace, ...rest } = currentFilters;

            const apiNames = {
                random: 'getAssignmentLeaveList',
                emailStatus: 'getEmailStatusList',
                rest: 'getLeavePromotionList',
                labor: 'getLeavePromotionList',
            };

            const totalCount = self.currentTabDataCount;
            const totalPages = Math.ceil(totalCount / self.size);

            // Start background loading
            self.setIsBackgroundLoading(true);

            // Create a new background run ID for this specific background operation
            self.backgroundFetchRunId = self.backgroundFetchRunId + 1;
            const backgroundRunId = self.backgroundFetchRunId;

            // Fire-and-forget background fetch
            (async () => {
                try {
                    for (let p = 2; p <= totalPages; p++) {
                        // Check if this background run was cancelled
                        if (backgroundRunId !== self.backgroundFetchRunId) {
                            break;
                        }

                        try {
                            const resp = await CompanyService[apiNames[self.filterParams.tab]]({
                                companyId,
                                page: p,
                                size: self.size,
                                filters: {
                                    ...rest,
                                    area,
                                    tab: self.filterParams.tab
                                }
                            });

                            const pageData = resp?.data || [];

                            // Only append if this background run is still current
                            if (pageData.length && backgroundRunId === self.backgroundFetchRunId) {
                                switch (self.filterParams.tab) {
                                    case 'rest':
                                        self.appendRestData(pageData);
                                        break;
                                    case 'random':
                                        self.appendRandomData(pageData);
                                        break;
                                    case 'labor':
                                        self.appendLaborData(pageData);
                                        break;
                                    case 'emailStatus':
                                        self.appendEmailStatusData(pageData);
                                        break;
                                    default:
                                        self.appendRestData(pageData);
                                }
                            }
                        } catch (e) {
                            console.error('Error loading background page', p, e);
                        }
                    }
                } finally {
                    // Only clear background loading if this run is still current
                    if (backgroundRunId === self.backgroundFetchRunId) {
                        self.setIsBackgroundLoading(false);
                    }
                }
            })();
        },

        async getLeaveDetail(userId, requestId) {
            try {
                const companyId = rootStore.company.get.id;
                const currentFilters = self.filterParams[self.filterParams.tab];
                const response = await CompanyService[
                    self.filterParams.tab === 'random'
                        ? 'getAssignmentDetail'
                        : 'getLeavePromotionDetail'
                    ]({
                    companyId,
                    userId,
                    year: currentFilters.selectedYear,
                    requestId: requestId || 0
                });

                self.setSelectedUser(response?.data?.user || {});
                if (self.filterParams.tab === 'random') {
                    return response?.data;
                }
                // Process and store the detail data
                const processedListData = processDataWithReplaceDataFields(response?.data?.list || []);
                self.setSelectedEmailDetail(processedListData);

                return response?.data;
            } catch (error) {
                console.error('Error getting leave detail:', error);
                return null;
            }
        },

        // Send Promotion Target Email
        async sendAnnualLeaveEmail({ emailType = 'ANN_VAC_PROM', emailOption = 'AL' }) {
            const companyId = rootStore.company.get.id;
            const userIds = self.userIdsForAction;

            try {
                const response = await CompanyService.sendAnnualLeavePromotion({
                    companyId,
                    userIds,
                    emailType,
                    emailOption,
                });

                self.getLeavePromotionList(); // Refresh list after sending email
                return response; // Return response if needed
            } catch (error) {
                console.error('Error sending annual leave promotion email:', error);
                throw error;
            }
        },

        // Get email send history details for Excel export
        async getPromotionDetails() {
            const companyId = rootStore.company.get.id;
            const currentFilters = self.filterParams[self.filterParams.tab];
            const userIds = self.checkedUsers.map(user => user.userId);
            try {
                const response = await CompanyService.getHistoryDetails({
                    companyId,
                    userIds,
                    year: currentFilters.selectedYear
                });
                if (response?.data?.length) {
                    const fullInfo = response.data.map(item => {
                        const user = item.user || {};
                        return item.list.map(detail => ({
                            ...pickUserNameFields(user),
                            ...detail
                        }));
                    }).flat();
                    const processedData = processDataWithReplaceDataFields(fullInfo);
                    return processedData;
                }
                return [];
            } catch (error) {
                console.error('Error getting history details:', error);
                throw error;
            }
        },
        getAssignmentDetails() {
            try {
                if (self.checkedUsers.length) {
                    const res = self.checkedUsers?.map(assign => {
                        const list = assign?.data?.list || [];
                        return list.map(item => {
                            return {
                                ...pickUserNameFields(assign),
                                ...item
                            }
                        });
                    });
                    return res.flat();
                }

                return [];
            } catch (error) {
                console.error('Error getting history details:', error);
                throw error;
            }
        },

        async assignRandomLeave({ vacationType = 'AL' }) {
            try {
                const companyId = rootStore.company.get.id;
                const userId = rootStore.user.me.id;
                const userIds = self.userIdsForAction;
                const group = await GroupService.getGroupInfoById(userId, companyId);
                const response = await CompanyService.sendRandomAssignment({
                    userIds,
                    companyId,
                    areaId: get(group, 'data.areaId', null),
                    vacationType
                });

                self.setSelectedDate(null);
                self.getLeavePromotionList(); // Refresh list after assignment

                return response;
            } catch (error) {
                console.error('Error assigning random leave:', error);
                throw error;
            }
        },
        // Initialize all data
        async initialize(presetFilters = null) {
            if (self.isLoading) return;
            try {
                await Promise.all([this.getYear(), this.getCommCodes(), this.getWorkPlace()]);
                if (presetFilters) {
                    const { targetEmployeeLabel, ...rest } = presetFilters;
                    const matched = targetEmployeeLabel
                        ? self.commCodeList.find((c) => c.rawLabel?.includes(targetEmployeeLabel))
                        : null;
                    this.setFilterParams({ ...rest, ...(matched ? { targetEmployee: matched.value } : {}) });
                }
                await this.getLeavePromotionList();
            } catch (error) {
                console.error('Error initializing AnnualLeavePromotion:', error);
            }
        },
    }));

export default AnnualLeavePromotion;
